import { inject, Type, ViewContainerRef } from '@angular/core';
import { NavigationExtras } from '@angular/router';
import { Arrayify, Throw } from '@sb/library/data-models';
import { BehaviorSubject, map, Observable, Subscription, switchMap, take, timer } from 'rxjs';
import { animationDuration } from './animations/animations';
import { NavigationType, Overwrite } from './definitions/base.definitions';
import { DrawerEvents } from './definitions/drawer.definitions';
import { DeviceTypeManager } from './managers/device-type.manager';
import { DialogData, DialogManager, DialogVoidy } from './managers/dialog.manager';
import { DrawerData, DrawerManager, DrawerVoidy } from './managers/drawer.manager';
import { RouterManager } from './managers/router.manager';
import { Storage } from './managers/storage.manager';
import { DialogBaseViewComponent } from './views/dialog-base-view.component';
import { DrawerBaseViewComponent } from './views/drawer-base-view.component';

export abstract class Base {
    protected static _DrawerManager: DrawerManager;
    protected static _DialogManager: DialogManager;
    protected static _RouterManager: RouterManager;
    protected static _DeviceTypeManager: DeviceTypeManager;

    private static _extendAnimation = new BehaviorSubject<void>(undefined);
    private static _animationDeadline = 0;

    // Extend all currently running transition animations
    static BeginTransitionAnimation() {
        Base._animationDeadline = Date.now() + animationDuration + 150;   // Extend past end of animation to ensure smoothness
        Base._extendAnimation.next();
    }

    // Guaranteed to fire, either after all transition animations are done, or immediately if none are running
    static get transitionAnimationEnd(): Observable<void> {
        return Base._extendAnimation.pipe(
            switchMap(() => timer(Base._animationDeadline - Date.now()).pipe(map(() => {}))),
            take(1)
        );
    }

    protected readonly _ViewContainer: ViewContainerRef | null = null;

    protected _subscriptions: Subscription[] = [];
    private _implementedScopes: typeof Scope[] = [];
    private _boundMethods: Record<string, Function> = {};

    CreateDialog<T extends DialogBaseViewComponent<any>>(componentType: Type<DialogVoidy<T>>, data: void, viewContainer?: ViewContainerRef | null): T;
    CreateDialog<T extends DialogBaseViewComponent<any>>(componentType: Type<T>, data: DialogData<T>, viewContainer?: ViewContainerRef | null): T;
    CreateDialog<T extends DialogBaseViewComponent<any>>(componentType: Type<T>, data: DialogData<T>, viewContainer = this._ViewContainer) {
        return Base._DialogManager.CreateDialog(componentType, data, viewContainer);
    }

    CreateDrawer<T extends DrawerBaseViewComponent<any>>(componentType: Type<DrawerVoidy<T>>, data: void, viewContainer?: ViewContainerRef | null): Observable<DrawerEvents>;
    CreateDrawer<T extends DrawerBaseViewComponent<any>>(componentType: Type<T>, data: DrawerData<T>, viewContainer?: ViewContainerRef | null): Observable<DrawerEvents>;
    CreateDrawer<T extends DrawerBaseViewComponent<any>>(componentType: Type<T>, data: DrawerData<T>, viewContainer = this._ViewContainer): Observable<DrawerEvents> {
        return Base._DrawerManager.CreateDrawer(componentType, data, viewContainer);
    }

    Navigate(url: string, extras?: NavigationExtras, navigationType: NavigationType = NavigationType.SameTab, params: string[] = []): Observable<void> {
        return Base._RouterManager.Navigate(url, extras, navigationType, params);
    }

    protected SetScope<I extends Scopes, R extends Scopes>(this: ScopeOf<I>, definition: { Implements?: I, Requires?: R }): ScopeType<R> {
        this._implementedScopes = Arrayify(definition.Implements);
        this._implementedScopes.forEach(scope => scope.AddComponent(this));

        const componentByKey: Record<string, Base & Record<string, any>> = {};
        Arrayify(definition.Requires).reverse().forEach(({ components, keys, name }) => {
            const component = components.find(c => c.constructor !== this.constructor && inject(c.constructor, { optional: true }) === c)
                            ?? Throw(`Could not find any ancestor that implements ${name}`);
            keys.forEach(key => componentByKey[key] = component);
        });

        return new Proxy({}, {
            get(_: any, key: string) {
                const component = componentByKey[key] ?? Throw(`${key} could not be found in any ancestor`);
                const result = component[key];
                return typeof result === 'function' && !component.hasOwnProperty(key) ? (component._boundMethods[key] ??= result.bind(component)) : result;
            }
        });
    }

    protected ConnectTrigger<T>(trigger: Observable<T> | undefined, method: (parameter: T) => any) {
        if (trigger) {
            this._subscriptions.push(trigger.subscribe(method.bind(this)));
        }
    }

    /**@internal*/ protected Destroy(): void {
        this._subscriptions.forEach(element => element?.unsubscribe());
        this._implementedScopes.forEach(scope => scope.RemoveComponent(this));
    }

    protected get DeviceTypeManager() {
        return Base._DeviceTypeManager;
    }

    protected get isMobileOrTablet(): boolean {
        return this.DeviceTypeManager.isMobileOrTablet;
    }

    protected get isMobile(): boolean {
        return this.DeviceTypeManager.isMobile;
    }

    protected get isTablet(): boolean {
        return this.DeviceTypeManager.isTablet;
    }

    protected get isTouchScreenDevice(): boolean {
        return this.DeviceTypeManager.isTouchScreenDevice;
    }

    protected get isDesktop(): boolean {
        return this.DeviceTypeManager.isDesktop;
    }

    protected get isStandalone(): boolean {
        return this.DeviceTypeManager.isStandalone;
    }
}

export const Field = <T>() => null as T; 

export class Scope {
    /**@internal*/ static keys: string[];
    /**@internal*/ private static _components: Base[];

    /**@internal*/ static get components() {
        if (!this.hasOwnProperty('_components')) {  // This Scope is being used for the first time; initialize.
            this._components = this !== Scope ? [] : Throw('Do not use Scope directly - make a subclass');
            this.keys = Object.keys(new this);
        }
        return this._components;
    }

    /**@internal*/ static AddComponent(component: Base) {
        this.components.unshift(component);
        this.superScope?.AddComponent(component);
    }

    /**@internal*/ static RemoveComponent(component: Base) {
        this.components.splice(this.components.indexOf(component), 1);
        this.superScope?.RemoveComponent(component);
    }

    /**@internal*/ private static get superScope(): typeof Scope | null {
        const superScope = Object.getPrototypeOf(this);   // Direct superclass of this
        return superScope !== Scope ? superScope : null;
    }
}

// Allow for all-optional scopes to be implemented by a class without any of the fields appearing
export interface Scope extends Base { }

type Scopes = typeof Scope | [typeof Scope, ...typeof Scope[]];
type ScopeOf<T> = T extends typeof Scope ? InstanceType<T> : T extends [infer U, ...infer V] ? Overwrite<ScopeOf<V>, ScopeOf<U>> : {};
type ScopeType<R> = Readonly<Omit<ScopeOf<R>, keyof Base>>;